import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useSelectTheme } from "@/contexts/SelectThemeContext";
import { useEffect, useState } from "react";
import { ShoppingBag, Bell, User, LogOut, Shield, Menu, X } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import R1617Mark from "@/components/R1617Mark";

export default function Nav() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { isSelect } = useSelectTheme();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isTempViewer = user?.role === "temp_viewer";

  // Close the mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const linkCls = ({ isActive }) =>
    `text-[13px] uppercase tracking-[0.22em] font-semibold transition-colors ${
      isSelect
        ? (isActive ? "text-[#F1CC70]" : "text-[#D0A455] hover:text-[#F1CC70]")
        : (isActive ? "text-charcoal" : "text-stone hover:text-charcoal")
    }`;

  const mobileLinkCls = ({ isActive }) =>
    `block w-full py-4 text-sm uppercase tracking-[0.24em] transition-colors ${
      isSelect
        ? `border-b border-[#D6C8A3] ${isActive ? "text-[#6E521A]" : "text-[#1B1614] hover:text-[#6E521A]"}`
        : `border-b border-borderSubtle ${isActive ? "text-oxblood" : "text-charcoal hover:text-oxblood"}`
    }`;

  const iconBtnCls = isSelect
    ? "text-[#1B1614] hover:text-[#6E521A] transition-colors"
    : "text-charcoal hover:text-oxblood transition-colors";

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-colors ${
        isSelect ? "bg-[#F1E5C7] border-[#D6C8A3]" : "bg-bone border-borderSubtle"
      }`}
      data-testid={isSelect ? "nav-select-mode" : "nav-default-mode"}
    >
      <div className={`max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between ${isSelect ? "h-28" : "h-20"}`}>
        {isSelect ? <R1617Mark /> : <BrandMark variant="dark" size="sm" />}

        <nav className="hidden md:flex items-center gap-8">
          {/* iter144 — Switch Tier removed. Buyers were mingling Select + Supply
              products in one cart which the fulfillment flow doesn't allow.
              Each tier is now a sealed lane. */}
          {!isTempViewer && <NavLink to="/orders" className={linkCls} data-testid="nav-orders">Orders</NavLink>}
          {!isTempViewer && (
            <NavLink to="/notifications" className={linkCls} data-testid="nav-notifications">
              <span className="inline-flex items-center gap-1.5"><Bell size={13} strokeWidth={1.5}/> Inbox</span>
            </NavLink>
          )}
          <NavLink to="/contact" className={linkCls} data-testid="nav-contact">Contact</NavLink>
          {isTempViewer && (
            <span
              className={`text-[13px] uppercase tracking-[0.22em] font-semibold ${isSelect ? "text-[#6E521A]" : "text-oxblood"}`}
              data-testid="nav-temp-badge"
            >
              Preview access
            </span>
          )}
          {user?.role === "admin" && (
            <NavLink to="/admin" className={linkCls} data-testid="nav-admin">
              <span className="inline-flex items-center gap-1.5"><Shield size={13} strokeWidth={1.5}/> Admin</span>
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-4 md:gap-5">
          {!isTempViewer && (
            <Link to="/account" className={`inline-flex items-center gap-2 ${iconBtnCls}`} data-testid="nav-account" aria-label="Account">
              <User size={18} strokeWidth={1.25}/>
              {user?.name && (
                <span className={`hidden lg:inline text-[13px] tracking-wide max-w-[140px] truncate ${isSelect ? "text-[#1B1614]" : ""}`} data-testid="nav-user-name">
                  {user.name}
                </span>
              )}
            </Link>
          )}
          {!isTempViewer && (
            <Link to="/cart" className={`relative ${iconBtnCls}`} data-testid="nav-cart" aria-label="Cart">
              <ShoppingBag size={18} strokeWidth={1.25}/>
              {count > 0 && (
                <span
                  className={`absolute -top-2 -right-2 text-[12px] leading-none px-1.5 py-0.5 font-mono rounded-full ${
                    isSelect ? "bg-[#DDA94E] text-black" : "bg-charcoal text-bone"
                  }`}
                  data-testid="cart-count"
                >
                  {count}
                </span>
              )}
            </Link>
          )}
          <button
            onClick={async () => { await logout(); nav("/enter"); }}
            className={`hidden md:inline-flex ${iconBtnCls}`}
            data-testid="nav-logout"
            aria-label="Sign out"
          >
            <LogOut size={18} strokeWidth={1.25}/>
          </button>
          {/* Mobile menu trigger */}
          <button
            onClick={() => setMobileOpen((o) => !o)}
            className={`md:hidden ${iconBtnCls}`}
            data-testid="nav-mobile-toggle"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={22} strokeWidth={1.5}/> : <Menu size={22} strokeWidth={1.5}/>}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <div
          className={`md:hidden border-t ${isSelect ? "border-[#D6C8A3] bg-[#F1E5C7]" : "border-borderSubtle bg-bone"}`}
          data-testid="nav-mobile-menu"
        >
          <div className="px-6 py-2">
            {/* iter144 — see comment above; tier switching removed globally. */}
            {!isTempViewer && <NavLink to="/orders" className={mobileLinkCls} data-testid="nav-mobile-orders">Orders</NavLink>}
            {!isTempViewer && (
              <NavLink to="/notifications" className={mobileLinkCls} data-testid="nav-mobile-notifications">
                <span className="inline-flex items-center gap-2"><Bell size={14} strokeWidth={1.5}/> Inbox</span>
              </NavLink>
            )}
            <NavLink to="/contact" className={mobileLinkCls} data-testid="nav-mobile-contact">Contact</NavLink>
            {user?.role === "admin" && (
              <NavLink to="/admin" className={mobileLinkCls} data-testid="nav-mobile-admin">
                <span className="inline-flex items-center gap-2"><Shield size={14} strokeWidth={1.5}/> Admin</span>
              </NavLink>
            )}
            {!isTempViewer && (
              <NavLink to="/account" className={mobileLinkCls} data-testid="nav-mobile-account">
                <span className="inline-flex items-center gap-2"><User size={14} strokeWidth={1.5}/> Account</span>
              </NavLink>
            )}
            <button
              onClick={async () => { await logout(); nav("/enter"); }}
              className={`block w-full py-4 text-sm uppercase tracking-[0.24em] text-left transition-colors ${
                isSelect ? "text-[#1B1614] hover:text-[#6E521A]" : "text-charcoal hover:text-oxblood"
              }`}
              data-testid="nav-mobile-logout"
            >
              <span className="inline-flex items-center gap-2"><LogOut size={14} strokeWidth={1.5}/> Sign out</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
